import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { showToast } from '@beatq/shared';
import {
  branchFromEnvelope,
  getBranch,
  isBranchPaused,
  pauseBranchTrading,
  resumeBranchTrading,
} from '../features/branch-settings/branchSettingsApi.js';
import { currentMerchantScope } from './merchantContext.js';

export function MerchantStoreStatusToggle() {
  const qc = useQueryClient();
  const { branchId } = currentMerchantScope();
  const query = useQuery({
    queryKey: ['merchant', 'branch', branchId],
    queryFn: () => getBranch(branchId),
    enabled: !!branchId,
    refetchInterval: 60_000,
  });
  const branch = branchFromEnvelope(query.data);
  const paused = isBranchPaused(branch);

  const setPaused = useMutation({
    mutationFn: async (nextPaused: boolean) => {
      if (nextPaused) {
        await pauseBranchTrading(branchId, {
          reason: 'Paused by manager',
          minutes: 30,
        });
        return;
      }
      await resumeBranchTrading(branchId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['merchant', 'branch', branchId] });
      void qc.invalidateQueries({ queryKey: ['merchant', 'branches'] });
      showToast('success', paused ? 'Store reopened' : 'Store paused');
    },
    onError: (err) => {
      showToast('error', err instanceof Error ? err.message : 'Could not update status');
    },
  });

  return (
    <button
      type="button"
      className="mer-header-pause"
      data-paused={paused ? 'true' : 'false'}
      data-testid="merchant-store-status"
      aria-label={paused ? 'Resume taking orders' : 'Pause taking orders'}
      disabled={setPaused.isPending || query.isLoading || query.isError || !branch || !branchId}
      onClick={() => setPaused.mutate(!paused)}
    >
      {paused ? 'Resume' : 'Pause'}
    </button>
  );
}
